import React from 'react';
import styled, {createGlobalStyle, useTheme} from 'styled-components';
import Select from 'react-select';
import { selectMenuCss, portalSelectStyles } from '../../theme/selectStyles';
import { ratingRangeStatus } from '../../service/constants';
import {
  sortingPriority,
  formRangeStatus,
  getInsightFilterLabel,
} from '../../service/data';

const POSITION_BY_PRIORITY = Object.fromEntries(
  Object.entries(sortingPriority).map(([label, value]) => [value, label])
);

const AGE_MIN = 15;
const AGE_MAX = 50;

const isBlankAge = (value) =>
  value === '' || value == null || Number.isNaN(Number(value));

const isInRange = (number, range) => {
  if (!Array.isArray(range)) return true;
  const lo = isBlankAge(range[0]) ? AGE_MIN : Number(range[0]);
  const hi = isBlankAge(range[1]) ? AGE_MAX : Number(range[1]);
  return number >= Math.min(lo, hi) && number <= Math.max(lo, hi);
};

const StyledSelectContainer = styled.div`
  width: 100%;
  margin: 5px 0;
  min-width: 0;
`;

const StyledAgeWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const StyledAgeCaption = styled.span`
  font-family: 'Archivo', sans-serif;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.textFaint};
`;

const StyledAgeRange = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
`;

const StyledAgeInput = styled.input`
  width: 100%;
  min-width: 0;
  height: 38px;
  box-sizing: border-box;
  padding: 0 10px;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 4px;
  background: ${({ theme }) => theme.card};
  color: ${({ theme }) => theme.text};
  font-family: 'Archivo', sans-serif;
  font-size: 14px;
  font-weight: 500;

  &::placeholder {
    color: ${({ theme }) => theme.textFaint};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.border};
  }

  /* Hide spinner so min/max stay compact */
  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const StyledAgeDash = styled.span`
  color: ${({ theme }) => theme.textFaint};
  font-size: 13px;
  flex-shrink: 0;
`;

const AgeRangeFilter = ({ filterValue, setFilter, header = 'Age' }) => {
  const minVal = Array.isArray(filterValue) ? filterValue[0] ?? '' : '';
  const maxVal = Array.isArray(filterValue) ? filterValue[1] ?? '' : '';

  const commit = (nextMin, nextMax) => {
    const emptyMin = isBlankAge(nextMin);
    const emptyMax = isBlankAge(nextMax);
    if (emptyMin && emptyMax) {
      setFilter(undefined);
      return;
    }
    // Keep empty fields empty while typing; defaults apply only in isInRange.
    setFilter([emptyMin ? '' : String(nextMin), emptyMax ? '' : String(nextMax)]);
  };

  return (
    <StyledAgeWrap>
      <StyledAgeCaption>{header}</StyledAgeCaption>
      <StyledAgeRange>
        <StyledAgeInput
          type="number"
          min={AGE_MIN}
          max={AGE_MAX}
          inputMode="numeric"
          placeholder="Min"
          value={minVal}
          onChange={(event) => commit(event.target.value, maxVal)}
          aria-label="Minimum age"
        />
        <StyledAgeDash>–</StyledAgeDash>
        <StyledAgeInput
          type="number"
          min={AGE_MIN}
          max={AGE_MAX}
          inputMode="numeric"
          placeholder="Max"
          value={maxVal}
          onChange={(event) => commit(minVal, event.target.value)}
          aria-label="Maximum age"
        />
      </StyledAgeRange>
    </StyledAgeWrap>
  );
};

const FilterMenuStyle = createGlobalStyle`
  .table-filter__menu-portal {
    ${selectMenuCss('table-filter')}
    .table-filter__menu {width:100%;max-width:calc(100vw - 24px);}
    .table-filter__option {white-space:normal;overflow-wrap:anywhere;}
  }
`;

const StyledSelect = styled(Select)`
  .table-filter__control {
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.border};
    color: ${({ theme }) => theme.text};
    box-shadow: none;
  }
  .table-filter__single-value,
  .table-filter__input-container {
    color: ${({ theme }) => theme.text};
  }
  .table-filter__placeholder {
    font-family: 'Archivo', sans-serif;
    font-size: 14.222px;
    line-height: 20px;
    font-weight: 500;
    letter-spacing: 0.5px;
    color: ${({ theme }) => theme.textFaint};
  }
  ${selectMenuCss('table-filter')}
`;

const DefaultColumnFilter = ({
  column: { Header, id, preFilteredRows, setFilter, filterValue, filterOptions },
}) => {
  const theme = useTheme();
  const handleMaxWidth = () => {
    const height = window.innerHeight;
    if (height <= 300) return 100;
    if (height <= 400) return 150;
    if (height <= 500) return 200;
    if (height <= 600) return 250;
    return 300;
  };

  const handleChange = (selectedOption) => {
    setFilter(selectedOption == null ? undefined : selectedOption.value);
  };

  const options = React.useMemo(() => {
    if (filterOptions) return filterOptions;
    const values = new Set();
    preFilteredRows.forEach((row) => {
      values.add(row.values[id]);
    });
    return Array.from(values.values())
      .filter((element) => element !== undefined && element !== null && element !== '')
      .sort((a, b) => {
        if (typeof a === 'number' && typeof b === 'number') return a - b;
        return String(a).localeCompare(String(b));
      })
      .map((element) => {
        if (id === 'sortPriority') {
          return { value: element, label: POSITION_BY_PRIORITY[element] || element };
        }
        if (id === 'ratingId' || id === 'potentialId') {
          return { value: element, label: ratingRangeStatus[element] };
        }
        if (id === 'formId') {
          return { value: element, label: formRangeStatus[element] || element };
        }
        const insightLabel = getInsightFilterLabel(id, element);
        return { value: element, label: insightLabel || element };
      });
  }, [id, preFilteredRows, filterOptions]);

  const selectedValue = React.useMemo(() => {
    if (filterValue === undefined || filterValue === null) return null;
    return (
      options.find(
        (option) =>
          option.value === filterValue ||
          String(option.value) === String(filterValue)
      ) || null
    );
  }, [filterValue, options]);

  if (id === 'age') {
    return (
      <StyledSelectContainer>
        <AgeRangeFilter filterValue={filterValue} setFilter={setFilter} header={Header} />
      </StyledSelectContainer>
    );
  }

  return (
    <StyledSelectContainer>
      <FilterMenuStyle/>
      <StyledSelect
        onChange={handleChange}
        options={options}
        value={selectedValue}
        menuPlacement="auto"
        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
        menuPosition="fixed"
        menuShouldScrollIntoView={false}
        minMenuHeight={70}
        styles={portalSelectStyles(theme)}
        maxMenuHeight={handleMaxWidth()}
        placeholder={Header}
        isClearable
        isSearchable={Header === 'Name'}
        classNamePrefix="table-filter"
        aria-label={`Filter by ${Header}`}
      />
    </StyledSelectContainer>
  );
};

export default DefaultColumnFilter;
export { isInRange, AgeRangeFilter };
